// src/components/common/AuthImage.jsx
import React, { useState, useEffect, useRef } from 'react';

/**
 * AuthImage component - Lazy loads images with optional authentication
 * Uses Intersection Observer to only load images when visible in viewport
 * For GCS signed URLs, loads directly without auth headers
 */
const AuthImage = ({ src, alt, className, fallback, rootMargin = '200px' }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once visible
        }
      },
      {
        rootMargin, // Start loading before element is fully visible
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [rootMargin]);

  // Load image once visible
  useEffect(() => {
    if (!isVisible || !src) {
      if (!src) {
        setLoading(false);
        setError(true);
      }
      return;
    }

    // GCS signed URLs (storage.googleapis.com) don't need auth - load directly
    // Legacy local URLs with /recipe-photos/ or /receipt-photos/ need auth
    const isGcsUrl = src.includes('storage.googleapis.com');
    const needsAuth = !isGcsUrl && (src.includes('/recipe-photos/') || src.includes('/receipt-photos/'));

    if (!needsAuth) {
      // Direct load for GCS signed URLs, blob URLs, data URLs
      setImageSrc(src);
      setLoading(false);
      return;
    }

    // Auth-required images (legacy local storage)
    let objectUrl = null;

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        const token = localStorage.getItem('token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(src, {
          headers,
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
        setLoading(false);
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        setError(true);
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup: revoke object URL when component unmounts
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, isVisible]);

  // Placeholder while waiting to become visible or loading
  if (!isVisible || loading) {
    return (
      <div
        ref={containerRef}
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6'
        }}
      >
        {isVisible && (
          <div className="animate-pulse" style={{ color: '#9ca3af' }}>...</div>
        )}
      </div>
    );
  }

  if (error || !imageSrc) {
    return fallback || <div ref={containerRef} className={className} style={{ backgroundColor: '#f3f4f6' }} />;
  }

  return <img ref={containerRef} src={imageSrc} alt={alt} className={className} loading="lazy" />;
};

export default AuthImage;
